import { SITE_URL } from "@/lib/schema";

const SHOPIFY_HOSTS = new Set(["ishvaram.com", "www.ishvaram.com"]);

const SHOPIFY_PATH_PREFIXES = [
  "/products",
  "/collections",
  "/pages",
  "/policies",
  "/cart",
  "/checkout",
  "/checkouts",
];

type ShopifyAttribution = {
  intent?: string;
  problem?: string;
  offer?: string;
  placement?: string;
  confidence?: number;
};

export type ShopifyCartProduct = {
  sku: string;
  shopify_variant_id?: string | number | null;
  variant_id?: string | number | null;
};

function normalizeSourcePath(pathname: string): string {
  const cleaned = pathname.split("?")[0].replace(/\/+$/, "");
  return cleaned || "/";
}

function sourceContent(pathname: string): string {
  return normalizeSourcePath(pathname).replace(/^\/+/, "").replace(/\/+/g, "__") || "home";
}

function getBrowserGoogleAnalyticsClientId(): string | null {
  if (typeof document === "undefined") return null;

  const gaCookie = document.cookie.split("; ").find((row) => row.startsWith("_ga="));
  if (!gaCookie) return null;

  const parts = gaCookie.split(".");
  if (parts.length < 4) return null;

  return `${parts[2]}.${parts[3]}`;
}

export function isShopifyHandoffUrl(rawHref: string, currentOrigin = SITE_URL): boolean {
  if (
    !rawHref ||
    rawHref.startsWith("#") ||
    rawHref.startsWith("mailto:") ||
    rawHref.startsWith("tel:")
  ) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(rawHref, currentOrigin);
  } catch {
    return false;
  }

  if (!SHOPIFY_HOSTS.has(url.hostname)) return false;
  if (
    SHOPIFY_PATH_PREFIXES.some(
      (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)
    )
  ) {
    return true;
  }

  return /^https?:\/\//.test(rawHref) && normalizeSourcePath(url.pathname) === "/";
}

export function withShopifyAttribution(
  rawHref: string,
  sourcePathname: string,
  attribution?: ShopifyAttribution,
  currentOrigin = SITE_URL
): string {
  if (!isShopifyHandoffUrl(rawHref, currentOrigin)) return rawHref;

  const url = new URL(rawHref, currentOrigin);
  const isSameOrigin = url.origin === new URL(currentOrigin).origin;
  if (isSameOrigin && !isCheckoutOrCartPath(url.pathname)) {
    url.search = "";
    return url.toString();
  }

  const sourcePath = normalizeSourcePath(sourcePathname);
  applyShopifyAttribution(url, sourcePath, attribution);
  return url.toString();
}

function isCheckoutOrCartPath(pathname: string): boolean {
  return (
    pathname === "/cart" ||
    pathname.startsWith("/cart/") ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/checkouts")
  );
}

function applyShopifyAttribution(
  url: URL,
  sourcePath: string,
  attribution?: ShopifyAttribution
): void {
  setFallbackParams(url, {
    utm_source: "seo_website",
    utm_medium: "organic_internal",
    utm_campaign: "seo_to_shopify",
    utm_content: sourceContent(sourcePath),
    ish_source_path: sourcePath,
    ish_handoff: "seo_to_shopify",
    ...optionalAttributionParams(attribution),
  });
  const gaClientId = getBrowserGoogleAnalyticsClientId();
  if (gaClientId) {
    setFallbackParam(url, "ish_ga_client_id", gaClientId);
  }
}

function optionalAttributionParams(
  attribution?: ShopifyAttribution
): Record<string, string | undefined> {
  return {
    ish_intent: attribution?.intent,
    ish_problem: attribution?.problem,
    ish_offer: attribution?.offer,
    ish_placement: attribution?.placement,
    ish_confidence:
      typeof attribution?.confidence === "number" ? String(attribution.confidence) : undefined,
  };
}

function setFallbackParams(url: URL, params: Record<string, string | undefined>): void {
  for (const [key, value] of Object.entries(params)) {
    if (value) setFallbackParam(url, key, value);
  }
}

function setFallbackParam(url: URL, key: string, value: string): void {
  url.searchParams.set(key, url.searchParams.get(key) || value);
}

function normalizeVariantId(product: ShopifyCartProduct): string | null {
  const rawVariantId = product.shopify_variant_id ?? product.variant_id;
  if (rawVariantId == null) return null;

  const value = String(rawVariantId).trim();
  if (!value) return null;

  const gidMatch = value.match(/ProductVariant\/(\d+)$/);
  return gidMatch?.[1] ?? value;
}

export function buildShopifyCartHref(
  products: ShopifyCartProduct[],
  sourcePathname: string,
  attribution?: ShopifyAttribution,
  currentOrigin = SITE_URL
): string | null {
  const cartItems = products
    .map(normalizeVariantId)
    .filter((variantId): variantId is string => Boolean(variantId))
    .map((variantId) => `${variantId}:1`);

  if (cartItems.length === 0) return null;

  return withShopifyAttribution(
    `${SITE_URL}/cart/${cartItems.join(",")}`,
    sourcePathname,
    attribution,
    currentOrigin
  );
}
